<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select class="input-label col-span-12 md:col-span-2"
                 v-model="selectedValue">
        <el-option key="quickView"
                   :label="$t('views.common.listView.quickView')"
                   value="quickView">
        </el-option>
        <el-option key="allItems" :label="$t('views.common.listView.allItems')"
                   value="allItems">
        </el-option>
        <el-option
            key="deletedItems"
            :label="$t('views.common.listView.deletedItems')"
            value="deletedItems"
        >
        </el-option>
        <el-option
            key="export"
            :label="$t('views.common.listView.selectedRows.export')"
            value="export"
        >
        </el-option>

        <el-option
            v-if="selectedValue != 'deletedItems'"
            key="delete"
            :label="$t('views.common.listView.selectedRows.delete')"
            value="delete"
        >
        </el-option>
        <el-option
            v-if="selectedValue == 'deletedItems'"
            key="restore"
            :label="$t('views.common.listView.selectedRows.restore')"
            value="restore"
        >
        </el-option>
      </el-select>

      <el-input
          :placeholder="$t('views.common.listView.searchRows')"
          class="w-full col-span-12 md:col-span-7"
          :prefix-icon="Search"
          v-model="searchTerm"
      />

      <div class="col-span-12 flex items-center gap-4 md:col-span-3">
        <horizontal-button
            :title="t('newDocumentation')"
            @click.prevent="$router.push($Route.CUSTOMERS_NOTES_NEW)"
        />
      </div>
    </div>

    <ag-grid-vue
        v-if="rowModelType"
        class="ag-theme-alpine mt-4 md:mt-0"
        :columnDefs="columnDefs"
        rowSelection="multiple"
        :enable-rtl="$ctx.getDir() === 'rtl'"
        style="height: 85vh"
        @cell-double-clicked="onCellDoubleClicked"
        :defaultColDef="defaultColDef"
        :pagination="selectedValue == 'allItems'"
        :paginationAutoPageSize="selectedValue == 'allItems'"
        :onGridReady="onGridReady"
        :rowBuffer="rowBuffer"
        :rowModelType="rowModelType"
        :cacheBlockSize="cacheBlockSize"
        :cacheOverflowSize="cacheOverflowSize"
        :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
        :infiniteInitialRowCount="infiniteInitialRowCount"
        :maxBlocksInCache="maxBlocksInCache"
    >
    </ag-grid-vue>

    <div class="mt-2">{{ $t('views.common.listView.count') }}: {{ count }}</div>
  </div>
</template>

<script lang="ts">
import {defineComponent, onBeforeMount, ref} from 'vue'

import {AgGridVue} from 'ag-grid-vue3'
import {
  ColDef,
  GridApi,
  GridOptions,
  IGetRowsParams,
  InfiniteRowModelModule,
  ModuleRegistry
} from 'ag-grid-community'
import HorizontalButton from '@/components/HorizontalButton.vue'

import Documentation, {
  documentationI18nMessages,
  DocumentationPaginated
} from '@/models/customer/Documentation'
import {useI18n} from 'vue-i18n'
import {formatPostgresDate} from '@/utils/date'
import {Search} from '@element-plus/icons-vue'
import NoteStarHeaderRenderer
  from "@/views/customers/notes/NoteStarHeaderRenderer.vue";
import NoteStarCellRenderer
  from "@/views/customers/notes/NoteStarCellRenderer.vue";
import {useStorage} from '@vueuse/core';

ModuleRegistry.registerModules([InfiniteRowModelModule])

export default defineComponent({
  name: 'notes',

  setup() {
    const {t} = useI18n({
      useScope: 'global',
      messages: documentationI18nMessages
    })

    return {t, Search}
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: Documentation[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}

    const rowBuffer = ref<any>(null)
    const rowSelection = ref<any>(null)
    const rowModelType = ref<any>(null)
    const cacheBlockSize = ref<any>(null)
    const cacheOverflowSize = ref<any>(null)
    const maxConcurrentDatasourceRequests = ref<any>(null)
    const infiniteInitialRowCount = ref<any>(null)
    const maxBlocksInCache = ref<any>(null)
    const starredFilter = ref<string>('')

    onBeforeMount(() => {
      rowBuffer.value = 0
      rowSelection.value = 'multiple'
      rowModelType.value = 'infinite'
      cacheBlockSize.value = 100
      cacheOverflowSize.value = 100
      maxConcurrentDatasourceRequests.value = 2
      infiniteInitialRowCount.value = 0
      maxBlocksInCache.value = 10
    })

    return {
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,

      searchTerm: useStorage('notes-search-term', ''),
      selectedValue: 'quickView',
      gridApi: undefined as GridApi | undefined,
      rowBuffer,
      rowSelection,
      rowModelType,
      cacheBlockSize,
      cacheOverflowSize,
      maxConcurrentDatasourceRequests,
      infiniteInitialRowCount,
      maxBlocksInCache,
      count: 0,
      starredFilter
    }
  },

  components: {
    AgGridVue,
    HorizontalButton,
  },

  async beforeMount() {
    this.defaultColDef = {
      sortable: false,
      filter: false,
      resizable: true,
      minWidth: 150
    }

    this.columnDefs = [
      {
        field: '-',
        hide: true,
        lockVisible: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },
      {
        headerName: `${this.t('documentationNumber')}`,
        field: 'externalID',
        cellRenderer: NoteStarCellRenderer,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        valueGetter: (params) => {
          const doc = params.node?.data as Documentation
          if (!doc) return ''
          return doc.externalID
        },
        cellRendererParams: () => ({}),
        headerComponent: NoteStarHeaderRenderer,
        headerComponentParams: () => ({
          headerName: `${this.t('documentationNumber')}`,
          starredFilter: this.starredFilter,
          setStarredFilter: (val) => {
            this.starredFilter = val;
          }
        })
      },
      {
        headerName: this.$t('models.common.date'),
        field: 'createdAtDate',
        cellStyle: {textAlign: 'center'},
        valueGetter: (params) => {
          if (!params.data) return ''
          const doc = params.node?.data as Documentation
          if (doc.createdAt) {
            return formatPostgresDate(String(doc.createdAt), 'DD/MM/YYYY')
          }
          return ''
        }
      },
      {
        headerName: this.$t('models.common.time'),
        field: 'createdAtTime',
        cellStyle: {direction: 'ltr !important', textAlign: 'center'},
        valueGetter: (params) => {
          if (!params.data) return ''
          const doc = params.node?.data as Documentation
          if (doc.createdAt) {
            return formatPostgresDate(String(doc.createdAt), 'hh:mm A')
          }
          return ''
        }
      },
      {
        headerName: this.t('name'),
        field: 'name',
        valueGetter: (params) => {
          if (!params.data) return ''
          const doc = params.node?.data as Documentation
          return doc.name || ''
        }
      },
      {
        headerName: this.t('subject'),
        field: 'subject',
        valueGetter: (params) => {
          if (!params.data) return ''
          const doc = params.node?.data as Documentation
          return doc.subject || ''
        }
      },
      {
        headerName: this.t('phone'),
        field: 'phone',
        cellStyle: {direction: 'ltr !important', textAlign: 'center'},
        valueGetter: (params) => {
          if (!params.data) return ''
          const doc = params.node?.data as Documentation
          return doc.phone || ''
        }
      },
      {
        headerName: this.t('taskExternalID'),
        valueGetter: (params) => {
          if (!params.data) return ''
          const doc = params.node?.data as Documentation
          return doc.task?.externalID || ''
        }
      },
      {
        headerName: this.t('taskDueDate'),
        cellStyle: {textAlign: 'center'},
        valueGetter: (params) => {
          if (!params.data) return ''
          const doc = params.node?.data as Documentation
          if (doc.task?.dueDate) {
            return formatPostgresDate(String(doc.task.dueDate), 'DD/MM/YYYY')
          }
          return ''
        }
      },
      {
        headerName: this.t('taskStatus'),
        cellStyle: (params) => {
          const doc = params.node?.data as Documentation
          const status = doc?.task?.status
          const bgMap: Record<string, string> = {
            todo:        '#f3f4f6',
            in_progress: '#fef9c3',
            done:        '#dcfce7',
            canceled:    '#fee2e2',
          }
          const colorMap: Record<string, string> = {
            todo:        '#374151',
            in_progress: '#854d0e',
            done:        '#166534',
            canceled:    '#991b1b',
          }
          if (!status) return {}
          return { background: bgMap[status] || '', color: colorMap[status] || '', fontWeight: '500' }
        },
        valueGetter: (params) => {
          if (!params.data) return ''
          const doc = params.node?.data as Documentation
          const status = doc.task?.status
          if (!status) return ''
          const labelMap: Record<string, string> = {
            todo: this.t('toDo'),
            in_progress: this.t('inProgress'),
            done: this.t('done'),
            canceled: this.t('canceled'),
          }
          return labelMap[status] || status
        }
      },
      {
        headerName: this.t('addedBy'),
        field: 'user',
        valueGetter: (params) => {
          if (!params.data) return ''
          const doc = params.node?.data as Documentation
          return doc.user?.name || ''
        }
      },
    ]

    try {
      this.count = (
          await this.$http.get<{
            count: number
          }>('/helpers/count?model=notes')
      ).count
      setTimeout(() => {
        this.filterResult()
      }, 10)
    } catch (error) {
      this.$alertModal.showDanger({title: error.title, body: error.body})
    }
  },

  mounted() {
    this.gridApi?.sizeColumnsToFit()
  },

  watch: {
    starredFilter: {
      handler() {
        this.updateStarredFilter();
      },
    },
    searchTerm() {
      this.filterResult()
    },
    async selectedValue(newValue, oldValue) {
      if (oldValue == 'delete' || oldValue == 'export' || oldValue == 'restore') {
        return
      }

      if (
          this.selectedValue === 'delete' ||
          this.selectedValue === 'export' ||
          this.selectedValue === 'restore'
      ) {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'delete') {
            await this.delete(selected)
            if (oldValue == 'quickView') {
              this.gridApi?.refreshInfiniteCache()
            } else {
              await this.updateRowData()
            }
          } else if (this.selectedValue === 'restore') {
            await this.restoreItems(selected)
            await this.updateRowData(oldValue == 'deletedItems')
          } else {
            this.gridApi?.exportDataAsCsv({onlySelected: true})
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          selected.length = 0
        }
        this.selectedValue = oldValue
      } else if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      } else if (this.selectedValue == 'deletedItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      }
    }
  },

  methods: {
    updateStarredFilter() {
      if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    },
    async updateRowData(softDeleted: boolean = false) {
      this.gridApi?.showLoadingOverlay()

      const urlParams = new URLSearchParams()
      if (this.starredFilter) {
        urlParams.set('filter', this.starredFilter)
      }

      try {
        const data = await this.$http.get<Documentation[]>(Documentation.ENDPOINT + `?${urlParams.toString()}`)
        if (softDeleted) {
          this.rowData = data
              .map((doc) => Documentation.from(doc))
              .filter((doc) => doc.deletedAt != null)
        } else {
          this.rowData = data
              .map((doc) => Documentation.from(doc))
              .filter((doc) => doc.deletedAt == null)
        }
        this.gridApi?.setGridOption('rowData', this.rowData)
      } catch (error) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }

      this.gridApi?.hideOverlay()
    },
    getDatasource() {
      const dataSource = {
        rowCount: undefined,
        getRows: async (params: IGetRowsParams) => {
          this.gridApi?.showLoadingOverlay()

          let url = `${Documentation.ENDPOINT}`

          const urlParams = new URLSearchParams()
          urlParams.set('startRow', params.startRow)
          urlParams.set('endRow', params.endRow)

          if (params.sortModel.length) {
            urlParams.set('colId', params.sortModel[0].colId)
            urlParams.set('sort', params.sortModel[0].sort)
          }

          if (Object.keys(params.filterModel).length) {
            urlParams.set('query', params.filterModel['-'].filter)
          }

          if (this.starredFilter) {
            urlParams.set('filter', this.starredFilter)
          }

          const res = await this.$http.get<DocumentationPaginated>(url + `?${urlParams.toString()}`)
          this.rowData = res.data.map((doc) => Documentation.from(doc))
          this.gridApi?.hideOverlay()

          const lastRow = res.total
          params.successCallback(this.rowData, lastRow)
        }
      }
      return dataSource
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      if (this.selectedValue == 'quickView') {
        this.gridApi?.setGridOption('datasource', this.getDatasource())
        this.gridApi?.setGridOption('serverSideSortOnServer', true)
      } else if (this.selectedValue == 'allItems') {
        this.gridApi?.setGridOption('serverSideSortOnServer', false)
        this.updateRowData()
      } else if (this.selectedValue == 'deletedItems') {
        this.gridApi?.setGridOption('serverSideSortOnServer', false)
        await this.updateRowData(true)
      }
    },
    async delete(documentations: Documentation[]) {
      if (confirm(this.$t('views.common.listView.selectedRows.deleteConfirm'))) {
        try {
          await this.$http.delete(Documentation.ENDPOINT, {data: documentations})
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.deleteSuccess')
          })
        } catch (error) {
          this.$alertModal.showDanger({
            title: error.title,
            body: error.body
          })
        }
      }
    },
    async restoreItems(documentations: Documentation[]) {
      if (confirm(this.$t('views.common.listView.selectedRows.restoreConfirm'))) {
        try {
          await this.$http.post('/helpers/restore-deleted', {
            data: documentations,
            table: 'notes'
          })
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.restoreSuccess')
          })
        } catch (error) {
          this.$alertModal.showDanger({
            title: error.title,
            body: error.body
          })
        }
      }
    },
    filterResult() {
      if (this.rowModelType == 'infinite') {
        this.gridApi?.setFilterModel({'-': {filter: this.searchTerm}})
        return
      }
      this.gridApi?.setQuickFilter(this.searchTerm)
    },
    onCellDoubleClicked(event: any) {
      this.$router.push(
          this.$Route.CUSTOMERS_NOTES_VIEW.replace(':id', event.data.id)
      )
    }
  }
})
</script>

<style></style>
